import { createContext, type ReactNode, useContext, useState } from 'react';
import type { WarehouseLocation } from 'src/api/warehouses.api';
import { useWarehouseSections, type WarehouseSectionsView } from './useWarehouseSections';

/** `null` closes the editor; `{ location: null }` opens it on a location that does not exist yet. */
export type LocationEditorState = { location: WarehouseLocation | null } | null;

type WarehousePageValue = {
  sections: WarehouseSectionsView;
  editModalOpen: boolean;
  setEditModalOpen: (open: boolean) => void;
  groupingsModalOpen: boolean;
  setGroupingsModalOpen: (open: boolean) => void;
  locationEditor: LocationEditorState;
  setLocationEditor: (state: LocationEditorState) => void;
};

const WarehousePageContext = createContext<WarehousePageValue | null>(null);

/** Owns the page's reads and its three modal states; the header opens modals the content renders. */
export function WarehousePageProviders({ warehouseId, children }: { warehouseId: number; children: ReactNode }) {
  const sections = useWarehouseSections(warehouseId);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [groupingsModalOpen, setGroupingsModalOpen] = useState(false);
  const [locationEditor, setLocationEditor] = useState<LocationEditorState>(null);

  return (
    <WarehousePageContext.Provider
      value={{
        sections,
        editModalOpen,
        setEditModalOpen,
        groupingsModalOpen,
        setGroupingsModalOpen,
        locationEditor,
        setLocationEditor,
      }}
    >
      {children}
    </WarehousePageContext.Provider>
  );
}

export function useWarehousePage(): WarehousePageValue {
  const value = useContext(WarehousePageContext);
  if (!value) throw new Error('useWarehousePage must be used inside WarehousePageProviders');
  return value;
}
